/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DialogIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import CustomDialogBoxV2 from "../../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import SelectDialogActionButtons from "./SelectDialogActionButtons";
import { Box } from "@mui/material";
import SelectEEDialog from "./SelectEEDialog";
import SelectLegalDialogMain from "./SelectLegalDialogMain";
import SelectTestCaseDialogMain from "./SelectTestCaseDialogMain";
import SelectSWMDialog from "./SelectSWMDialog";
import SelectVehicleSystemDialog from "./SelectVehicleSystemDialog";

const displayPageMap = {
  ls: "ls",
  lc: "lc",
  lr: "lr",
};

function getDialogTitle(entityType, ee, swm, vehicleDialogTitle) {
  if (entityType === ee) {
    return "Select EE Component";
  }
  if (entityType === swm) {
    return "Select Software Module";
  }
  return vehicleDialogTitle;
}

function DialogIndex({
  openEESelect,
  setOpenEESelect,
  isZonalProject,
  zoneList,
  onClickAdd,
  selectedEEMain,
  openLegalSelect,
  setOpenLegalSelect,
  selectedLegalMain,
  testCaseEntityType,
  testCaseEntityId,
  disabledLegalRequirement = [],
  shouldShowLegalRequirementLinkedInfo = () => false,
  onClickAddLegal,
  onClickAddTestCase,
  openTestCaseSelect,
  setOpenTestCaseSelect,
  entityType,
  entityID,
  selectedTextCaseMain,
  savedLinkages,
  disableLrRadioButton,
  disabledEEComponents = [],
  disabledSWMComponents = [],
  disabledVSComponents = [],
  onSelectEEForSWM = () => { },
  selectedVSMain,
  selectedSWMMain,
  prefillContext = null,
  aiRecommendedEntities = [],
  requireVehicleComponentSelection = true,
  vehicleDialogTitle = "Select Vehicle System",
}) {
  const zone = "zone";
  const ee = "ee";
  const swm = "swm";
  const vs = "vs";
  const vc = "vc";

  const [showZone, setShowZone] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedZoneName, setSelectedZoneName] = useState(null);
  const [selectedEE, setSelectedEE] = useState([]);
  const [selectedSWM, setSelectedSWM] = useState([]);
  const [selectedVS, setSelectedVS] = useState([]);
  const [selectedVC, setSelectedVC] = useState([]);
  const [selectedLegal, setSelectedLegal] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedLegalStandard, setSelectedLegalStandard] = useState(null);
  const [legalChapterList, setLegalChapterList] = useState([]);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState([]);

  const [displayPage, setDisplayPage] = useState(displayPageMap.ls);
  const [selectedTestCase, setSelectedTestCase] = useState([]);

  const persistentVSRef = useRef(selectedVSMain ?? []);

  const prevOpenRef = useRef(false);

  const isEditMode = selectedVSMain && selectedVSMain.length > 0;

  useEffect(() => {
    if (isZonalProject) {
      setShowZone(zone);
    } else {
      switch (entityType) {
        case ee:
          setShowZone(ee);
          break;
        case swm:
          setShowZone(ee);
          break;
        case vs:
          setShowZone(vs);
          break;
        default:
          break;
      }
    }
  }, [isZonalProject, openEESelect]);

  useEffect(() => {
    persistentVSRef.current = selectedVS;
  }, [selectedVS]);

  useEffect(() => {
    if (openEESelect && !prevOpenRef.current) {
      if (selectedVSMain && selectedVSMain.length > 0) {
        persistentVSRef.current = selectedVSMain;
        setSelectedVS(selectedVSMain);
      } else {
        persistentVSRef.current = [];
        setSelectedVS([]);
      }
    }
    prevOpenRef.current = openEESelect;
  }, [openEESelect]); // intentionally exclude selectedVSMain to avoid re-running mid-session

  useEffect(() => {
    if (selectedVSMain && selectedVSMain.length > 0) {
      persistentVSRef.current = selectedVSMain;
      if (openEESelect) {
        setSelectedVS(selectedVSMain);
      }
    }
  }, [selectedVSMain]); // intentionally exclude openEESelect — only trigger on actual parent change

  useEffect(() => {
    if (openEESelect && !prevOpenRef.current) {
      if (selectedEEMain && selectedEEMain.length > 0) {
        setSelectedEE(selectedEEMain);
      } else {
        setSelectedEE([]);
      }
    }
  }, [openEESelect, selectedEEMain]);

  useEffect(() => {
    if (openEESelect && !prevOpenRef.current) {
      if (selectedSWMMain && selectedSWMMain.length > 0) {
        setSelectedSWM(selectedSWMMain);
      } else {
        setSelectedSWM([]);
      }
    }
  }, [openEESelect, selectedSWMMain]);

  useEffect(() => {
    if (openLegalSelect) {
      if (selectedLegalMain && selectedLegalMain.length > 0) {
        setSelectedLegal(selectedLegalMain);
        if (legalChapterList && legalChapterList.length > 0) {
          setDisplayPage(displayPageMap.lc);
        } else {
          setDisplayPage(displayPageMap.ls);
        }
      } else {
        setSelectedLegal([]);
        setDisplayPage(displayPageMap.ls);
      }
    }
  }, [openLegalSelect, selectedLegalMain]); // intentionally exclude legalChapterList — it changes during navigation and must not reset displayPage

  useEffect(() => {
    if (!openTestCaseSelect) {
      return;
    }

    const syncedTestCases = Array.isArray(selectedTextCaseMain)
      ? selectedTextCaseMain
      : [];

    setSelectedTestCase(syncedTestCases);
    setSelectedTestCaseIds(
      syncedTestCases
        .map((item) => item?.test_case_id)
        .filter(
          (testCaseId) => testCaseId !== undefined && testCaseId !== null,
        ),
    );
  }, [openTestCaseSelect, selectedTextCaseMain]);

  return (
    <>
      <CustomDialogBoxV2
        title={getDialogTitle(entityType, ee, swm, vehicleDialogTitle)}
        open={openEESelect}
        onClose={() => {
          setOpenEESelect(false);
          setSelectedEE([]);
          if (entityType === vs) {
            persistentVSRef.current = selectedVSMain ?? [];
            setSelectedVS(selectedVSMain ?? []);
          }
        }}
        dialogActions={
          <SelectDialogActionButtons
            displayBack={
              showZone !== zone &&
              ((isZonalProject && showZone === ee) ||
                showZone === swm ||
                (isZonalProject && showZone === vs) ||
                showZone === vc)
            }
            enableSubmit={(() => {
              switch (entityType) {
                case ee:
                  return selectedEE && selectedEE.length > 0 && showZone === ee;
                case swm:
                  return selectedSWM && selectedSWM.length > 0 && showZone === swm;
                case vs:
                  return (
                    selectedVS &&
                    selectedVS.length > 0 &&
                    (!requireVehicleComponentSelection ||
                      !selectedVS.some((vs) => !vs.vcData?.length)) &&
                    showZone !== zone
                  );
                default:
                  return false;
              }
            })()}
            onClickBack={() => {
              if (showZone === vc) {
                setShowZone(vs);
              } else if (showZone === swm) {
                setShowZone(ee);
              } else {
                setShowZone(zone);
                switch (entityType) {
                  case ee:
                    setSelectedEE(selectedEEMain || []);
                    break;
                  case swm:
                    setSelectedSWM(selectedSWMMain || []);
                    break;
                  case vs:
                    break;
                  default:
                    break;
                }
              }
            }}
            onClickAdd={() => {
              switch (entityType) {
                case ee:
                  onClickAdd(selectedEE);
                  break;
                case swm:
                  onClickAdd(selectedSWM);
                  break;
                case vs:
                  persistentVSRef.current = [];
                  onClickAdd(selectedVS);
                  break;
                default:
                  break;
              }
            }}
            onClickCancel={() => {
              setOpenEESelect(false);
              switch (entityType) {
                case ee:
                  setSelectedEE(selectedEEMain || []);
                  break;
                case swm:
                  setSelectedSWM(selectedSWMMain || []);
                  break;
                case vs:
                  persistentVSRef.current = selectedVSMain ?? [];
                  setSelectedVS(selectedVSMain ?? []);
                  break;
                default:
                  break;
              }
            }}
          />
        }
      >
        <Box sx={{ width: "450px", height: "370px" }}>
          <Box>
            <Box>
              {entityType === "ee" && (
                <SelectEEDialog
                  isZonalProject={isZonalProject}
                  zoneList={zoneList}
                  setSelectedZone={setSelectedZone}
                  setSelectedZoneName={setSelectedZoneName}
                  selectedZoneName={selectedZoneName}
                  selectedZone={selectedZone}
                  showZone={showZone}
                  setShowZone={setShowZone}
                  selectedEE={selectedEE}
                  setSelectedEE={setSelectedEE}
                  // disabledEEComponents={disabledEEComponents}
                />
              )}
              {entityType === "swm" && (
                <SelectSWMDialog
                  isZonalProject={isZonalProject}
                  zoneList={zoneList}
                  setSelectedZone={setSelectedZone}
                  selectedZone={selectedZone}
                  setSelectedZoneName={setSelectedZoneName}
                  selectedZoneName={selectedZoneName}
                  showZone={showZone}
                  setShowZone={setShowZone}
                  selectedEE={selectedEE}
                  setSelectedEE={setSelectedEE}
                  selectedSWM={selectedSWM}
                  setSelectedSWM={setSelectedSWM}
                  // disabledSWMComponents={disabledSWMComponents}
                  onSelectEEForSWM={onSelectEEForSWM}
                />
              )}

              {entityType === "vs" && (
                <SelectVehicleSystemDialog
                  isZonalProject={isZonalProject}
                  zoneList={zoneList}
                  setSelectedZone={setSelectedZone}
                  selectedZone={selectedZone}
                  setSelectedZoneName={setSelectedZoneName}
                  selectedZoneName={selectedZoneName}
                  showZone={showZone}
                  setShowZone={setShowZone}
                  selectedVS={selectedVS}
                  setSelectedVS={setSelectedVS}
                  selectedVC={selectedVC}
                  setSelectedVC={setSelectedVC}
                  // disabledVSComponents={disabledVSComponents}
                  selectedLegalMain={selectedLegalMain}
                  requireVehicleComponentSelection={
                    requireVehicleComponentSelection
                  }
                  isEditMode={isEditMode}
                />
              )}
            </Box>
          </Box>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        title={"Select Legal Requirement:"}
        open={openLegalSelect}
        onClose={() => {
          setOpenLegalSelect(false);
          setSelectedLegal(selectedLegalMain || []);
          setDisplayPage(displayPageMap.ls);
        }}
        dialogActions={
          <SelectDialogActionButtons
            displayBack={displayPage !== displayPageMap.ls}
            disableBack={displayPage === displayPageMap.lc && selectedLegal && selectedLegal.length > 0}
            enableSubmit={selectedLegal && selectedLegal.length > 0}
            onClickBack={() => {
              if (displayPage === displayPageMap.lc) {
                setDisplayPage(displayPageMap.ls);
              }
              if (displayPage === displayPageMap.lr) {
                setDisplayPage(displayPageMap.lc);
              }
            }}
            onClickAdd={() => {
              onClickAddLegal(selectedLegal);
              setDisplayPage(displayPageMap.ls);
            }}
            onClickCancel={() => {
              setOpenLegalSelect(false);
              setSelectedLegal(selectedLegalMain || []);
              setDisplayPage(displayPageMap.ls);
            }}
          />
        }
      >
        <Box sx={{ width: "450px", height: "370px" }}>
          <Box>
            <Box>
              <SelectLegalDialogMain
                disableLrRadioButton={disableLrRadioButton}
                displayPageMap={displayPageMap}
                setSelectedLegal={setSelectedLegal}
                selectedLegal={selectedLegal}
                displayPage={displayPage}
                setDisplayPage={setDisplayPage}
                savedLinkages={savedLinkages}
                // disabledLegalRequirement={disabledLegalRequirement}
                shouldShowLegalRequirementLinkedInfo={
                  shouldShowLegalRequirementLinkedInfo
                }
                selectedStandard={selectedStandard}
                setSelectedStandard={setSelectedStandard}
                selectedChapter={selectedChapter}
                setSelectedChapter={setSelectedChapter}
                selectedLegalStandard={selectedLegalStandard}
                setSelectedLegalStandard={setSelectedLegalStandard}
                legalChapterList={legalChapterList}
                setLegalChapterList={setLegalChapterList}
                isAISuggested={Boolean(prefillContext)}
                aiRecommendedEntities={aiRecommendedEntities}
              />
            </Box>
          </Box>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        title={"Select Test Case:"}
        open={openTestCaseSelect}
        onClose={() => {
          setOpenTestCaseSelect(false);
          setSelectedTestCase(selectedTextCaseMain);
        }}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={selectedTestCase && selectedTestCase.length > 0}
            onClickAdd={() => {
              onClickAddTestCase(selectedTestCase);
            }}
            onClickCancel={() => {
              setOpenTestCaseSelect(false);
              setSelectedTestCase(selectedTextCaseMain);
            }}
          />
        }
      >
        <Box sx={{ width: "450px", height: "370px" }}>
          <Box>
            <Box>
              <SelectTestCaseDialogMain
                open={openTestCaseSelect}
                selectedTestCase={selectedTestCase}
                setSelectedTestCase={setSelectedTestCase}
                setSelectedTestCaseIds={setSelectedTestCaseIds}
                selectedTestCaseIds={selectedTestCaseIds}
                selectedLegalMain={selectedLegalMain}
                entityType={testCaseEntityType ?? entityType}
                entityID={testCaseEntityId ?? entityID}
                lrID={selectedLegalMain?.lr_id}
                selectedEEMain={selectedEEMain}
                selectedSWMMain={selectedSWMMain}
                selectedVSMain={selectedVSMain}
              />
            </Box>
          </Box>
        </Box>
      </CustomDialogBoxV2>
    </>
  );
}

DialogIndex.propTypes = {
  openEESelect: PropTypes.bool,
  setOpenEESelect: PropTypes.func,
  isZonalProject: PropTypes.bool,
  zoneList: PropTypes.array,
  onClickAdd: PropTypes.func,
  selectedEEMain: PropTypes.array,
  openLegalSelect: PropTypes.bool,
  setOpenLegalSelect: PropTypes.func,
  selectedLegalMain: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  testCaseEntityType: PropTypes.string,
  testCaseEntityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabledLegalRequirement: PropTypes.array,
  shouldShowLegalRequirementLinkedInfo: PropTypes.func,
  onClickAddLegal: PropTypes.func,
  onClickAddTestCase: PropTypes.func,
  openTestCaseSelect: PropTypes.bool,
  setOpenTestCaseSelect: PropTypes.func,
  entityType: PropTypes.string,
  entityID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedTextCaseMain: PropTypes.array,
  savedLinkages: PropTypes.array,
  disableLrRadioButton: PropTypes.bool,
  disabledEEComponents: PropTypes.array,
  disabledSWMComponents: PropTypes.array,
  disabledVSComponents: PropTypes.array,
  onSelectEEForSWM: PropTypes.func,
  selectedVSMain: PropTypes.array,
  selectedSWMMain: PropTypes.array,
  prefillContext: PropTypes.object,
  aiRecommendedEntities: PropTypes.array,
  requireVehicleComponentSelection: PropTypes.bool,
  vehicleDialogTitle: PropTypes.string,
};

export default DialogIndex;
